import { createContext, type FC, type PropsWithChildren, useContext } from "react";

/**
 * The numeric shape of a rendered transcript — how much the renderer is being asked to hold, not
 * what it holds. Counts and a serialized-size estimate only: never message text, tool inputs, or
 * tool outputs.
 */
export interface ChatTranscriptDimensions {
  sessionId: string;
  /** Rendered transcript entries (the AI SDK's `messages` list). */
  entryCount: number;
  /** Total parts across those entries — the tool-heavy dimension `entryCount` hides. */
  partCount: number;
  /**
   * Approximate serialized size in bytes, omitted when the transcript could not be serialized.
   * Derived from a JSON string's length (UTF-16 code units), so it under-counts multi-byte text.
   */
  estimatedBytes?: number;
}

/**
 * The host application's telemetry seam. `@qw/chat` measures the transcript; the host decides where
 * the numbers go. Keeping the sink behind this interface is what lets the library stay free of any
 * analytics dependency — inbox forwards to PostHog, web mounts nothing.
 */
export interface ChatMetricsObserver {
  onTranscriptDimensions: (dimensions: ChatTranscriptDimensions) => void;
  /** The session stopped being rendered (switched away from, or unmounted). */
  onSessionEnd: (sessionId: string) => void;
}

const ChatMetricsContext = createContext<ChatMetricsObserver | null>(null);

export const ChatMetricsProvider: FC<PropsWithChildren<{ observer: ChatMetricsObserver }>> = ({
  observer,
  children,
}) => <ChatMetricsContext.Provider value={observer}>{children}</ChatMetricsContext.Provider>;

/** `null` where no host adapter is mounted — measurement is opt-in per application. */
export const useChatMetricsObserver = () => useContext(ChatMetricsContext);
